import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Trash2, HeartPulse, Activity, Pencil, BookmarkPlus, Check, Loader2 } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { Skeleton } from '../../components/ui/Skeleton'
import { EmptyState } from '../../components/EmptyState'
import ImagePreviewDialog from '../../components/ImagePreviewDialog'
import PullToRefresh from '../../components/PullToRefresh'
import ExercisePlaceholder from './components/ExercisePlaceholder'
import { useSessionDetail } from '../../hooks/useSessionDetail'
import { useWorkoutTemplates } from '../../hooks/useWorkoutTemplates'
import { useConfirm } from '../../contexts/ConfirmDialogContext'
import { performRefresh } from '../../lib/refresh'

const TEMPLATABLE_SESSION_TYPES = new Set([
  'day1_chest_back',
  'day2_arms_shoulders',
  'day3_legs',
])
import WorkoutBackHeader from './components/WorkoutBackHeader'

function formatDate(iso, lang) {
  if (!iso) return ''
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString(lang || 'en', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  } catch {
    return iso
  }
}

function MetaPill({ label, value }) {
  if (value == null || value === '') return null
  return (
    <div className="rounded-md bg-muted px-2.5 py-1.5">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-sm font-medium text-foreground">{value}</p>
    </div>
  )
}

export default function SessionDetail() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams()
  const { session, loading, deleteSession, refetch } = useSessionDetail(id)
  const { setTemplate } = useWorkoutTemplates()
  const handleRefresh = useCallback(() => performRefresh([refetch]), [refetch])
  const confirm = useConfirm()
  const [previewExercise, setPreviewExercise] = useState(null)
  const [templateState, setTemplateState] = useState('idle') // 'idle' | 'saving' | 'saved' | 'error'

  useEffect(() => {
    if (templateState !== 'saved' && templateState !== 'error') return undefined
    const timer = setTimeout(() => setTemplateState('idle'), 3000)
    return () => clearTimeout(timer)
  }, [templateState])

  const canSaveAsTemplate =
    !!session &&
    TEMPLATABLE_SESSION_TYPES.has(session.session_type) &&
    (session.exercise_logs?.length || 0) > 0

  const handleSaveAsTemplate = async () => {
    if (!canSaveAsTemplate) return
    const dayLabel = t(`workout.sessionType.${session.session_type}`)
    const ok = await confirm({
      title: t('workout.sessionDetail.saveAsTemplateConfirm', { day: dayLabel }),
      description: t('workout.sessionDetail.saveAsTemplateConfirmDesc', { day: dayLabel }),
      confirmLabel: t('workout.sessionDetail.saveAsTemplate'),
      icon: BookmarkPlus,
    })
    if (!ok) return
    setTemplateState('saving')
    const ids = [...session.exercise_logs]
      .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
      .map((l) => l.exercise_id)
      .filter(Boolean)
    const success = await setTemplate(session.session_type, ids)
    setTemplateState(success ? 'saved' : 'error')
  }

  const handleDelete = async () => {
    const ok = await confirm({
      title: t('workout.sessionDetail.confirmDelete'),
      description: t('workout.sessionDetail.confirmDeleteDesc'),
      confirmLabel: t('workout.sessionDetail.delete'),
      variant: 'destructive',
      icon: Trash2,
    })
    if (!ok) return
    const success = await deleteSession()
    if (success) navigate('/workout/history')
  }

  if (loading) {
    return (
      <div className="space-y-4 content-fade">
        <Skeleton className="h-10 w-1/2" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    )
  }

  if (!session) {
    return (
      <div className="space-y-4 content-fade">
        <WorkoutBackHeader title={t('workout.sessionDetail.notFound')} fallback="/workout/history" />
      </div>
    )
  }

  const date = formatDate(session.date, i18n.language)
  const typeLabel = t(`workout.sessionType.${session.session_type}`, { defaultValue: session.session_type })

  // Sum kcal across cardio blocks so the recap card surfaces the
  // session total alongside body weight. Strength sessions usually
  // skip this — only render when there's a positive total.
  const totalCalories = (session.cardio_logs || []).reduce(
    (sum, c) => sum + (Number(c.calories_estimated) || 0),
    0,
  )

  return (
    <PullToRefresh onRefresh={handleRefresh}>
    <div className="space-y-4 content-fade">
      <WorkoutBackHeader title={typeLabel} subtitle={date} fallback="/workout/history" />

      {/* Meta pills */}
      {(session.duration_min || session.body_weight_kg || session.energy_rating || session.start_time || totalCalories > 0) && (
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <MetaPill
                label={t('workout.sessionDetail.duration')}
                value={session.duration_min ? `${session.duration_min} min` : null}
              />
              <MetaPill
                label={t('workout.sessionDetail.bodyWeight')}
                value={session.body_weight_kg ? `${session.body_weight_kg} kg` : null}
              />
              <MetaPill
                label={t('workout.sessionDetail.totalCalories')}
                value={totalCalories > 0 ? `${totalCalories} kcal` : null}
              />
              <MetaPill
                label={t('workout.sessionDetail.energyLabel')}
                value={session.energy_rating ? `${session.energy_rating} / 10` : null}
              />
              <MetaPill
                label={t('workout.sessionDetail.startTime')}
                value={session.start_time || null}
              />
            </div>
            {session.notes && (
              <div className="mt-3 pt-3 border-t border-border">
                <p className="text-xs text-muted-foreground mb-1">{t('workout.sessionDetail.notes')}</p>
                <p className="text-sm text-foreground whitespace-pre-wrap">{session.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Exercise logs */}
      {session.exercise_logs.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-1">
            {t('workout.sessionDetail.exercises')}
          </p>
          {session.exercise_logs.map((log) => (
            <Card key={log.id}>
              <CardContent className="p-3 sm:p-4 space-y-2">
                <div className="flex items-start gap-3">
                  {log.exercise?.photoUrl ? (
                    <button
                      type="button"
                      onClick={() => setPreviewExercise(log.exercise)}
                      aria-label={t('workout.session.previewExercise', { name: log.exercise?.name || '' })}
                      className="w-12 h-12 rounded-md overflow-hidden bg-white flex-shrink-0 border border-border hover:border-app-primary-500/60 transition-colors"
                    >
                      <img
                        src={log.exercise.photoUrl}
                        alt=""
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ) : (
                    <div className="w-12 h-12 rounded-md overflow-hidden bg-white flex-shrink-0 border border-border">
                      <ExercisePlaceholder
                        muscleGroup={log.exercise?.muscle_group}
                        iconClassName="w-5 h-5"
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0 space-y-2">
                    <Link
                      to={`/workout/exercises/${log.exercise_id}`}
                      className="text-sm font-semibold text-foreground hover:text-app-primary-500 truncate block"
                    >
                      {log.exercise?.name || t('workout.session.missingExercise')}
                    </Link>
                    {log.sets.length > 0 && (
                      <table className="w-full text-xs">
                        <thead className="text-muted-foreground">
                          <tr>
                            <th className="text-start font-normal w-6">#</th>
                            <th className="text-start font-normal">{t('workout.set.weight')}</th>
                            <th className="text-start font-normal">{t('workout.set.reps')}</th>
                            <th className="text-start font-normal">{t('workout.set.rpe')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {log.sets.map((s) => (
                            <tr key={s.id} className="border-t border-border">
                              <td className="py-1.5 text-foreground">{s.set_number}</td>
                              <td className="py-1.5 text-foreground font-mono"><span dir="ltr">{Number(s.weight_kg).toLocaleString(undefined, { maximumFractionDigits: 1 })}</span></td>
                              <td className="py-1.5 text-foreground font-mono"><span dir="ltr">{s.reps}</span></td>
                              <td className="py-1.5 text-muted-foreground">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  {s.rpe != null && (
                                    <span className="font-mono" dir="ltr">{s.rpe}</span>
                                  )}
                                  {s.is_warmup && (
                                    <span className="px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-[10px] font-medium">
                                      {t('workout.sessionDetail.warmupTag')}
                                    </span>
                                  )}
                                  {s.is_failure && (
                                    <span className="px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-[10px] font-medium">
                                      {t('workout.sessionDetail.failureTag')}
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
                {log.notes && (
                  <p className="text-xs text-muted-foreground whitespace-pre-wrap pt-1">{log.notes}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Cardio blocks */}
      {session.cardio_logs.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-1">
            {t('workout.sessionDetail.cardio')}
          </p>
          {session.cardio_logs.map((c) => (
            <Card key={c.id} className="border-blue-500/30">
              <CardContent className="p-3 sm:p-4 space-y-1.5">
                <div className="flex items-center gap-2">
                  <HeartPulse className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  <p className="text-sm font-semibold text-foreground">
                    {t(`workout.cardioType.${c.cardio_type}`, { defaultValue: c.cardio_type })}
                  </p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                  <span className="text-foreground">{t('workout.cardio.duration')}: <span className="font-mono" dir="ltr">{c.duration_min}</span></span>
                  {c.level != null && <span className="text-foreground">{t('workout.cardio.level')}: <span className="font-mono" dir="ltr">{c.level}</span></span>}
                  {c.incline != null && <span className="text-foreground">{t('workout.cardio.incline')}: <span className="font-mono" dir="ltr">{c.incline}</span></span>}
                  {c.speed != null && <span className="text-foreground">{t('workout.cardio.speed')}: <span className="font-mono" dir="ltr">{c.speed}</span></span>}
                  {c.distance_km != null && <span className="text-foreground">{t('workout.cardio.distance')}: <span className="font-mono" dir="ltr">{c.distance_km}</span></span>}
                  {c.calories_estimated != null && <span className="text-foreground">{t('workout.cardio.calories')}: <span className="font-mono" dir="ltr">{c.calories_estimated}</span></span>}
                </div>
                {c.notes && <p className="text-xs text-muted-foreground whitespace-pre-wrap">{c.notes}</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Empty body case */}
      {session.exercise_logs.length === 0 && session.cardio_logs.length === 0 && (
        <EmptyState
          icon={Activity}
          title={t('workout.sessionDetail.noLogs')}
        />
      )}

      {/* Promote this session's exercise list to the day's template */}
      {canSaveAsTemplate && (
        <Button
          onClick={handleSaveAsTemplate}
          disabled={templateState === 'saving'}
          variant="outline"
          className="w-full"
        >
          {templateState === 'saving' ? (
            <Loader2 className="w-4 h-4 me-2 animate-spin" />
          ) : templateState === 'saved' ? (
            <Check className="w-4 h-4 me-2 text-green-500" />
          ) : (
            <BookmarkPlus className="w-4 h-4 me-2" />
          )}
          {templateState === 'saved'
            ? t('workout.sessionDetail.saveAsTemplateSuccess')
            : templateState === 'error'
              ? t('workout.sessionDetail.saveAsTemplateError')
              : t('workout.sessionDetail.saveAsTemplate')}
        </Button>
      )}

      {/* Edit + Delete */}
      <div className="flex gap-2">
        <Button
          onClick={() => navigate(`/workout/new?edit=${session.id}`)}
          variant="outline"
          className="flex-1"
        >
          <Pencil className="w-4 h-4 me-2" />
          {t('workout.sessionDetail.edit')}
        </Button>
        <Button
          onClick={handleDelete}
          variant="outline"
          className="flex-1 text-red-500 border-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
        >
          <Trash2 className="w-4 h-4 me-2" />
          {t('workout.sessionDetail.delete')}
        </Button>
      </div>

      <ImagePreviewDialog
        open={!!previewExercise?.photoUrl}
        src={previewExercise?.photoUrl}
        alt={previewExercise?.name || ''}
        onClose={() => setPreviewExercise(null)}
      />
    </div>
    </PullToRefresh>
  )
}
